import { Component, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Product, NewProduct } from '../../../../interfaces/product.interface';
import { InsuranceCompany } from '../../../../interfaces/insurance-company.interface';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/components/confirm-dialog/confirm-dialog';

export interface ProductDialogData {
  editingProduct?: Product | null;
  formData: NewProduct;
  insuranceCompanies: InsuranceCompany[];
  isSubmitting?: boolean;
}

export interface ProductDialogResult {
  action: 'save' | 'delete' | 'cancel';
  data?: NewProduct;
}

@Component({
  selector: 'app-product-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule
],
  template: `
    <div class="product-dialog crm-dialog">
      <h2 mat-dialog-title>
        <mat-icon>{{ data.editingProduct ? 'edit' : 'inventory_2' }}</mat-icon>
        {{ data.editingProduct ? 'Termék szerkesztése' : 'Új termék hozzáadása' }}
      </h2>
    
      <mat-dialog-content>
        <form class="product-form">
          <!-- Biztosító kiválasztása -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Biztosító társaság</mat-label>
            <mat-select [(ngModel)]="formData.insurancecompanyid"
              name="insuranceCompany"
              required
              [disabled]="isSubmitting">
              @for (company of data.insuranceCompanies; track company) {
                <mat-option
                  [value]="company.id">
                  {{ company.companyname }}
                </mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>business</mat-icon>
          </mat-form-field>
    
          <!-- Módozat kód -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Módozat kód</mat-label>
            <input matInput
              [(ngModel)]="formData.productcode"
              name="productCode"
              required
              placeholder="pl. LAK001"
              [disabled]="isSubmitting">
            <mat-icon matSuffix>tag</mat-icon>
          </mat-form-field>
    
          <!-- Termék neve -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Módozat neve</mat-label>
            <input matInput
              [(ngModel)]="formData.productname"
              name="productName"
              required
              placeholder="pl. Lakásbiztosítás Alapcsomag"
              [disabled]="isSubmitting">
            <mat-icon matSuffix>description</mat-icon>
          </mat-form-field>
    
          <!-- Termék típusa -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Típus</mat-label>
            <mat-select [(ngModel)]="formData.producttype"
              name="productType"
              required
              [disabled]="isSubmitting">
              <mat-option value="Lakásbiztosítás">Lakásbiztosítás</mat-option>
              <mat-option value="Gépjármű-biztosítás">Gépjármű-biztosítás</mat-option>
              <mat-option value="Életbiztosítás (kockázati)">Életbiztosítás (kockázati)</mat-option>
              <mat-option value="Életbiztosítás (UL)">Életbiztosítás (UL)</mat-option>
              <mat-option value="Életbiztosítás (UL nyugdíj)">Életbiztosítás (UL nyugdíj)</mat-option>
              <mat-option value="Életbiztosítás (Hagyományos nyugdíj)">Életbiztosítás (Hagyományos nyugdíj)</mat-option>
              <mat-option value="Baleset-biztosítás">Baleset-biztosítás</mat-option>
              <mat-option value="Utasbiztosítás">Utasbiztosítás</mat-option>
              <mat-option value="Egészségbiztosítás">Egészségbiztosítás</mat-option>
              <mat-option value="Vagyonbiztosítás">Vagyonbiztosítás</mat-option>
              <mat-option value="Felelősségbiztosítás">Felelősségbiztosítás</mat-option>
              <mat-option value="Egyéb">Egyéb</mat-option>
            </mat-select>
            <mat-icon matSuffix>category</mat-icon>
          </mat-form-field>
        </form>
      </mat-dialog-content>
    
      <mat-dialog-actions align="end">
        <!-- Törlés gomb (csak szerkesztés esetén) -->
        @if (data.editingProduct) {
          <button
            mat-button
            color="warn"
            (click)="onDelete()"
            [disabled]="isSubmitting"
            class="delete-button">
            <mat-icon>delete</mat-icon>
            Törlés
          </button>
        }
    
        <div class="spacer"></div>
    
        <!-- Mégse gomb -->
        <button mat-button (click)="onCancel()" [disabled]="isSubmitting">
          Mégse
        </button>
    
        <!-- Mentés gomb -->
        <button mat-raised-button
          color="primary"
          (click)="onSave()"
          [disabled]="!isFormValid() || isSubmitting">
          @if (!isSubmitting) {
            <mat-icon>{{ data.editingProduct ? 'save' : 'add' }}</mat-icon>
          }
          @if (isSubmitting) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          {{ data.editingProduct ? 'Mentés' : 'Hozzáadás' }}
        </button>
      </mat-dialog-actions>
    </div>
    `,
  styles: [`
    .product-dialog {
      width: 100%;
      max-width: 500px;
    }

    .product-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 16px 0;
    }

    .full-width {
      width: 100%;
    }

    .spacer {
      flex: 1;
    }

    .delete-button {
      margin-right: auto;
    }

    mat-dialog-content {
      min-height: 280px;
      overflow-y: visible;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      margin: 0;
      min-height: 52px;
    }

    mat-form-field {
      margin-bottom: 8px;
    }

    mat-spinner {
      margin-right: 8px;
    }

    /* CRM Dialog styling consistency */
    .crm-dialog h2 {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #2c3e50;
      font-size: 20px;
      font-weight: 500;
      margin: 0;
      padding: 24px 24px 16px;
      border-bottom: 1px solid #e0e0e0;
    }

    .crm-dialog h2 mat-icon {
      // color: #2196F3;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    /* Responsive design */
    @media (max-width: 600px) {
      .product-dialog {
        max-width: 100vw;
        width: 100vw;
        margin: 0;
      }

      .product-form {
        padding: 12px 0;
        gap: 12px;
      }

      mat-dialog-content {
        min-height: 240px;
      }

      mat-dialog-actions {
        padding: 12px 16px;
        flex-wrap: wrap;
        gap: 8px;
      }

      .delete-button {
        margin-right: 0;
        width: 100%;
        order: 3;
      }
    }
  `]
})
export class ProductDialog {
  formData: NewProduct;
  isSubmitting = false;

  constructor(
    public dialogRef: MatDialogRef<ProductDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ProductDialogData,
    private dialog: MatDialog
  ) {
    this.formData = { ...data.formData };
    this.isSubmitting = data.isSubmitting || false;
  }

  isFormValid(): boolean {
    return !!(
      this.formData.insurancecompanyid &&
      this.formData.productcode?.trim() &&
      this.formData.productname?.trim() &&
      this.formData.producttype?.trim()
    );
  }

  onSave(): void {
    if (this.isFormValid()) {
      this.dialogRef.close({
        action: 'save',
        data: this.formData
      } as ProductDialogResult);
    }
  }

  onDelete(): void {
    if (!this.data.editingProduct) {
      return;
    }

    const deleteDialogData: ConfirmDialogData = {
      title: 'Termék törlése',
      message: `Biztosan törölni szeretné a "${this.data.editingProduct.productname}" (${this.data.editingProduct.productcode}) terméket?`
    };

    const deleteDialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: deleteDialogData
    });

    deleteDialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // A felhasználó megerősítette a törlést
        this.dialogRef.close({
          action: 'delete'
        } as ProductDialogResult);
      }
      // Ha result === false vagy undefined, akkor nem történik semmi (megse)
    });
  }

  onCancel(): void {
    this.dialogRef.close({
      action: 'cancel'
    } as ProductDialogResult);
  }
}